/**
 * One line of the catalogue: where it is, who is waiting for it, what is
 * coming, and everything that has ever happened to it.
 *
 * The movement list is the page's centre rather than a tab at the bottom. A
 * stock figure is an assertion; the movements are the evidence for it, and the
 * two are shown together so that a figure somebody disputes can be walked back
 * to the day it changed and the person who changed it.
 */
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useSession } from "../../app/SessionContext";
import {
  categoryLabel,
  STATE_LABEL,
  available,
  consumed,
  daysOfCover,
  onHand,
  onOrder,
  reserved,
  rowOf,
  stateOf,
  type StoresRegister,
} from "../../domain/stores";
import { incomingFor } from "../../domain/storesDesk";
import {
  MOVEMENT_LABEL,
  adjust,
  count,
  issue,
  releaseReservation,
  reserve,
  returnToStore,
  transfer,
  type Result,
} from "../../domain/storesEdit";
import { Button, Card, EmptyState, Field, Input, Select, Table } from "../../design/components";
import { Page } from "../../layout/AppShell";
import { x as ex } from "../executive/shared";
import { Dialog } from "./Purchasing";
import { BinLabel } from "./BinLabel";
import { dueText } from "./StoresPage";
import { useStores } from "./useStores";
import { t, tn } from "../../i18n";
import s from "./Stores.module.css";

type Action = "issue" | "return" | "receive_adjust" | "count" | "transfer" | "reserve";

export function StoreItemPage() {
  const { sku = "" } = useParams();
  const session = useSession();
  const entered = useStores();
  const register = entered.value;
  const [action, setAction] = useState<Action | null>(null);
  const [refused, setRefused] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const item = register.items.find((i) => i.sku === sku);
  const now = new Date();
  const row = useMemo(() => (item ? rowOf(item, register, now) : null), [item, register]);

  const apply = async (result: Result) => {
    setAction(null);
    if (!result.ok) {
      setRefused(result.why);
      setDone(null);
      return;
    }
    setRefused(null);
    setDone(result.note ?? t("Recorded."));
    const no = await entered.commit(result);
    if (no) {
      setRefused(no);
      setDone(null);
    }
  };

  if (!item || !row) {
    return (
      <Page eyebrow={t("{site} · Stores", { site: session.site.code })} title="Not in the catalog">
        <EmptyState>
          {t("{sku} is not a line in this catalog.", { sku })} <Link to="/stores">{t("Back to the stores")}</Link>
        </EmptyState>
      </Page>
    );
  }

  const movements = register.movements
    .filter((m) => m.sku === sku)
    .slice()
    .sort((a, b) => b.at.localeCompare(a.at));
  const reservations = register.reservations.filter((r) => r.sku === sku && !r.released);
  const incoming = incomingFor(sku, register);

  return (
    <Page
      eyebrow={t("{site} · Stores", { site: session.site.code })}
      title={item.name}
      actions={
        <div className={s.actions}>
          <Button variant="primary" onClick={() => setAction("issue")}>{t("Issue")}</Button>
          <Button onClick={() => setAction("return")}>{t("Return")}</Button>
          <Button onClick={() => setAction("reserve")}>{t("Reserve for a job")}</Button>
          <Button onClick={() => setAction("count")}>{t("Count")}</Button>
          <Button onClick={() => setAction("transfer")}>{t("Transfer")}</Button>
          <Button onClick={() => setAction("receive_adjust")}>{t("Adjust")}</Button>
        </div>
      }
    >
      <p className={s.lede}>
        <span className={s.sku}>{item.sku}</span> · {t(categoryLabel(item.category, register))}
        {item.manufacturer && ` · ${item.manufacturer}`}
        {item.partNumber && ` ${item.partNumber}`}
      </p>
      {item.critical && (
        <p className={s.note}>
          {t("A critical spare: {why}", { why: item.critical.why })}
        </p>
      )}
      {refused && (
        <p className={s.refused} role="alert" data-testid="item-refused">
          {refused}
        </p>
      )}
      {done && <p className={s.saved} data-testid="item-done">{done}</p>}

      <Card title="Where it stands" sub="on hand, promised and available are three different numbers">
        <Table>
          <tbody>
            <tr>
              <th>{t("State")}</th>
              <td>
                <span className={s.state} data-state={row.state}>{t(STATE_LABEL[row.state])}</span>
              </td>
            </tr>
            <tr>
              <th>{t("On hand")}</th>
              <td className={s.num}>{`${row.onHand} ${item.uom}`}</td>
            </tr>
            <tr>
              <th>{t("Promised to open jobs")}</th>
              <td className={s.num}>{row.reserved}</td>
            </tr>
            <tr>
              <th>{t("Available to the next job")}</th>
              <td className={`${s.num} ${row.available <= 0 ? s.bad : ""}`}>{row.available}</td>
            </tr>
            <tr>
              <th>{t("On order")}</th>
              <td className={s.num}>
                {row.onOrder > 0 ? `${row.onOrder} · ${row.expectedAt ? dueText(row.expectedAt) : t("no date given")}` : "—"}
              </td>
            </tr>
            <tr>
              <th>{t("Reorder point / maximum")}</th>
              <td className={s.num}>{`${item.min} / ${item.max} ${item.uom}`}</td>
            </tr>
            <tr>
              <th>{t("Used in the last year")}</th>
              <td className={s.num}>{`${row.consumed365} ${item.uom}`}</td>
            </tr>
            <tr>
              <th>{t("Days of cover")}</th>
              <td className={s.num}>
                {row.cover === null ? (
                  <span className={s.unknown}>{t("unknown — nothing has been drawn, so there is no rate")}</span>
                ) : (
                  tn(Math.round(row.cover), "{n} day", "{n} days")
                )}
              </td>
            </tr>
            <tr>
              <th>{t("Lead time")}</th>
              <td className={s.num}>
                {item.leadDays === undefined ? (
                  <span className={s.unknown}>{t("not recorded")}</span>
                ) : (
                  tn(item.leadDays, "{n} day", "{n} days")
                )}
              </td>
            </tr>
            <tr>
              <th>{t("Last counted")}</th>
              <td>
                {item.counted ? (
                  t("{at} by {by}, {variance}", {
                    at: item.counted.at,
                    by: item.counted.by,
                    variance:
                      item.counted.variance === 0
                        ? t("agreed with the register")
                        : t("differed by {n}", { n: item.counted.variance }),
                  })
                ) : (
                  <span className={s.unknown}>{t("never counted")}</span>
                )}
              </td>
            </tr>
            {item.expiresAt && (
              <tr>
                <th>{t("Shelf life")}</th>
                <td>{dueText(item.expiresAt)}</td>
              </tr>
            )}
            <tr>
              <th>{t("Supplier")}</th>
              <td>
                {item.supplier ?? <span className={s.unknown}>{t("not recorded")}</span>}
                {item.contractRef && <div className={s.why}>{t("under {ref}", { ref: item.contractRef })}</div>}
              </td>
            </tr>
            {item.serves && (
              <tr>
                <th>{t("Serves")}</th>
                <td className={s.serves}>
                  {[...(item.serves.assetTypes ?? []), ...(item.serves.fleetKinds ?? []), ...(item.serves.trades ?? [])].join(
                    ", ",
                  ) || t("nothing named")}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
        {item.note && <p className={s.meta}>{item.note}</p>}
      </Card>

      <Card title="In which store" sub="the shelf, bin by bin">
        <ul className={s.bins}>
          {item.bins.map((b, i) => (
            <li key={`${b.location}-${b.bin ?? i}`}>
              {register.locations.find((l) => l.code === b.location)?.name ?? b.location}
              {b.bin && ` · ${b.bin}`} — <span className={s.num}>{`${b.onHand} ${item.uom}`}</span>
            </li>
          ))}
        </ul>
      </Card>

      <Card
        title="The bin label"
        sub="Print it for the shelf; the counter scans it"
        actions={
          <button type="button" className={ex.tag} onClick={() => window.print()}>
            {t("Print")}
          </button>
        }
      >
        <BinLabel item={item} locations={register.locations} />
        <p className={s.meta}>
          {item.barcode
            ? t(
                "This bin already carries a barcode of its own — {code} — so the label uses it. The counter finds the part by either.",
                { code: item.barcode },
              )
            : t(
                "The label shows the catalog number. If the part has a maker's barcode, record it on the line; the counter finds either.",
              )}
        </p>
      </Card>

      <Card title="Who is waiting for it" sub="open reservations against work orders">
        {reservations.length === 0 ? (
          <EmptyState>{t("No open job has this line reserved.")}</EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>{t("Work order")}</th>
                <th className={s.num}>{t("Wanted")}</th>
                <th className={s.num}>{t("Issued")}</th>
                <th>{t("Needed")}</th>
                <th>{t("Reserved by")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {reservations.map((r) => (
                <tr key={r.id}>
                  <td className={s.sku}>{r.workOrder}</td>
                  <td className={s.num}>{r.qty}</td>
                  <td className={s.num}>{r.issued}</td>
                  <td>{r.neededAt ? dueText(r.neededAt) : <span className={s.unknown}>{t("no date")}</span>}</td>
                  <td>{r.by}</td>
                  <td>
                    <Button
                      size="sm"
                      onClick={() =>
                        void apply(
                          releaseReservation(register, r.id, t("released from the stores desk"), {
                            name: session.principal.name,
                          }),
                        )
                      }
                    >
                      {t("Release")}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {incoming.length > 0 && (
        <Card title="What is coming" sub="a supplier's promise, which is not stock">
          <Table>
            <thead>
              <tr>
                <th>{t("Order")}</th>
                <th>{t("Supplier")}</th>
                <th className={s.num}>{t("Outstanding")}</th>
                <th>{t("Expected")}</th>
              </tr>
            </thead>
            <tbody>
              {incoming.map((o) => (
                <tr key={o.number}>
                  <td className={s.sku}>{o.number}</td>
                  <td>{o.supplier}</td>
                  <td className={s.num}>{o.qty}</td>
                  <td>{o.expectedAt ? dueText(o.expectedAt) : <span className={s.unknown}>{t("no date given")}</span>}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      <Card title="Everything that has happened to it" sub="Corrections are added as new movements">
        {movements.length === 0 ? (
          <EmptyState>{t("Nothing has moved yet.")}</EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>{t("When")}</th>
                <th>{t("What")}</th>
                <th className={s.num}>{t("Quantity")}</th>
                <th>{t("Store")}</th>
                <th>{t("By")}</th>
                <th>{t("Against")}</th>
              </tr>
            </thead>
            <tbody data-testid="item-movements">
              {movements.map((m) => (
                <tr key={m.id}>
                  <td className={s.when}>{m.at.slice(0, 10)}</td>
                  <td>{t(MOVEMENT_LABEL[m.kind])}</td>
                  <td className={s.num}>{m.qty > 0 ? `+${m.qty}` : m.qty}</td>
                  <td>{m.location}{m.bin && ` · ${m.bin}`}</td>
                  <td>{m.by}</td>
                  <td>
                    {m.workOrder ?? m.reference ?? ""}
                    {m.note && <div className={s.why}>{m.note}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <p className={ex.saved}>{entered.savedTo}</p>

      {action && (
        <ActionDialog
          action={action}
          register={register}
          sku={sku}
          who={session.principal.name}
          onClose={() => setAction(null)}
          onApply={apply}
        />
      )}
    </Page>
  );
}

const TITLE: Record<Action, string> = {
  issue: "Issue to a job",
  return: "Return to the shelf",
  receive_adjust: "Adjust the figure",
  count: "Count what is on the shelf",
  transfer: "Move between stores",
  reserve: "Reserve for a job",
};

function ActionDialog({
  action,
  register,
  sku,
  who,
  onClose,
  onApply,
}: {
  action: Action;
  register: StoresRegister;
  sku: string;
  who: string;
  onClose: () => void;
  onApply: (r: Result) => void | Promise<void>;
}) {
  const item = register.items.find((i) => i.sku === sku)!;
  const here = item.bins[0]?.location ?? register.locations[0]?.code ?? "";
  const [qty, setQty] = useState("1");
  const [location, setLocation] = useState(here);
  const [toLocation, setToLocation] = useState(
    register.locations.find((l) => l.code !== here)?.code ?? here,
  );
  const [workOrder, setWorkOrder] = useState("");
  const [neededAt, setNeededAt] = useState("");
  const [reason, setReason] = useState("");
  const actor = { name: who };
  const n = Number(qty);

  const run = () => {
    switch (action) {
      case "issue":
        return issue(register, { sku, qty: n, location, workOrder: workOrder || undefined, note: reason || undefined }, actor);
      case "return":
        return returnToStore(register, { sku, qty: n, location, workOrder: workOrder || undefined }, actor);
      case "reserve":
        return reserve(register, { sku, qty: n, workOrder, neededAt: neededAt || undefined }, actor);
      case "count":
        return count(register, { sku, location, counted: n, note: reason || undefined }, actor);
      case "transfer":
        return transfer(register, { sku, qty: n, from: location, to: toLocation, note: reason || undefined }, actor);
      case "receive_adjust":
        return adjust(register, { sku, qty: n, location, reason }, actor);
    }
  };

  return (
    <Dialog onClose={onClose} testId="stores-action-dialog" title={t(TITLE[action])}>
      <p className={s.meta}>{`${item.sku} — ${item.name}`}</p>
      <div className={s.formRow}>
        <Field
          label={
            action === "count"
              ? t("Counted quantity")
              : action === "receive_adjust"
                ? t("Change by (negative to reduce)")
                : t("Quantity")
          }
        >
          <Input
            type="number"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            aria-label={t("Quantity")}
            data-testid="stores-action-qty"
          />
        </Field>
        {action !== "reserve" && (
          <Field label={action === "transfer" ? t("From") : t("Store")}>
            <Select value={location} onChange={(e) => setLocation(e.target.value)} aria-label={t("Store")}>
              {register.locations.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {action === "transfer" && (
          <Field label={t("To")}>
            <Select value={toLocation} onChange={(e) => setToLocation(e.target.value)} aria-label={t("To")}>
              {register.locations.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {(action === "issue" || action === "return" || action === "reserve") && (
          <Field label={t("Work order")}>
            <Input
              value={workOrder}
              onChange={(e) => setWorkOrder(e.target.value)}
              placeholder="WO-000231"
              aria-label={t("Work order")}
              data-testid="stores-action-wo"
            />
          </Field>
        )}
        {action === "reserve" && (
          <Field label={t("Needed by")}>
            <Input type="date" value={neededAt} onChange={(e) => setNeededAt(e.target.value)} aria-label={t("Needed by")} />
          </Field>
        )}
      </div>
      {(action === "receive_adjust" || action === "count" || action === "issue" || action === "transfer") && (
        <Field label={action === "receive_adjust" ? t("Why (required)") : t("Note")}>
          <textarea
            className={s.textarea}
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            aria-label={t("Note")}
            data-testid="stores-action-note"
          />
        </Field>
      )}
      {action === "count" && (
        <p className={s.meta}>
          {t("The register says {n} {uom} in this store. A count that agrees is recorded too — it is the only evidence the accuracy figure has.", {
            n: item.bins.filter((b) => b.location === location).reduce((a, b) => a + b.onHand, 0),
            uom: item.uom,
          })}
        </p>
      )}
      <div className={s.formFoot}>
        <Button onClick={onClose}>{t("Cancel")}</Button>
        <Button variant="primary" data-testid="stores-action-confirm" onClick={() => void onApply(run())}>
          {t("Record it")}
        </Button>
      </div>
    </Dialog>
  );
}

/* re-exported so the desk and this page cannot drift on how a figure is read */
export { available, consumed, daysOfCover, onHand, onOrder, reserved, stateOf };
